import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminVerification from "@/pages/AdminVerification";
import AdminImports from "@/pages/AdminImports";
import AdminOnboarding from "@/pages/AdminOnboarding";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminReports from "@/pages/AdminReports";
import AdminAiUsage from "@/pages/AdminAiUsage";
import RefundReviews from "@/pages/RefundReviews";
import AdminConnectMigration from "@/pages/AdminConnectMigration";

// Existing vendor category codes stored on vendor docs. Labels are display-only.
const VENDOR_CATEGORY_LABELS = {
  catering: "Catering",
  venue: "Venue",
  restaurant: "Restaurant",
  photography: "Photography",
  florist: "Florist",
  transportation: "Transportation",
  dj: "DJ",
  rentals: "Rentals",
  av: "AV",
  security: "Security",
};

// Filter a list of docs whose `created_at` is an ISO string against a date-age window.
function withinDateWindow(iso, window) {
  if (!window || window === "all") return true;
  if (!iso) return false;
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return false;
  const now = Date.now();
  const days = (now - created) / (1000 * 60 * 60 * 24);
  if (window === "7d") return days <= 7;
  if (window === "30d") return days <= 30;
  if (window === "older") return days > 30;
  return true;
}

function sortDocs(list, sortKey, nameGetter = (d) => d.name || "") {
  const arr = [...list];
  if (sortKey === "newest") {
    arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  } else if (sortKey === "oldest") {
    arr.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  } else if (sortKey === "az") {
    arr.sort((a, b) => nameGetter(a).localeCompare(nameGetter(b), undefined, { sensitivity: "base" }));
  } else if (sortKey === "za") {
    arr.sort((a, b) => nameGetter(b).localeCompare(nameGetter(a), undefined, { sensitivity: "base" }));
  }
  return arr;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(false);
  const loadUsers = () => {
    setUsersError(false);
    api.get("/admin/users").then(({ data }) => setUsers(Array.isArray(data) ? data : [])).catch(() => setUsersError(true));
  };
  const [vendors, setVendors] = useState([]);
  const [refundReviewCount, setRefundReviewCount] = useState(0);
  const [openReportsCount, setOpenReportsCount] = useState(0);
  const [migrationEnabled, setMigrationEnabled] = useState(false);
  const [suspendUser, setSuspendUser] = useState(null);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendBusy, setSuspendBusy] = useState(false);

  // --- Vendors tab filter state (default sort: newest first — new registrations first) ---
  const [vSearch, setVSearch] = useState("");
  const [vCategory, setVCategory] = useState("all");
  const [vWindow, setVWindow] = useState("all");
  const [vSort, setVSort] = useState("newest");

  // --- Users tab filter state ---
  const [uSearch, setUSearch] = useState("");
  const [uRole, setURole] = useState("all");
  const [uWindow, setUWindow] = useState("all");
  const [uSort, setUSort] = useState("newest");

  const load = () => {
    api.get("/admin/stats").then(({ data }) => { setStats(data); setMigrationEnabled(!!data.connect_owner_migration_enabled); }).catch(() => {});
    loadUsers();
    api.get("/vendors", { params: { limit: 200, include_unpublished: true } }).then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/refund-reviews").then(({ data }) => setRefundReviewCount(data.length)).catch(() => setRefundReviewCount(0));
    api.get("/admin/reports", { params: { status: "open" } })
      .then(({ data }) => setOpenReportsCount(data.length))
      .catch(() => setOpenReportsCount(0));
  };
  useEffect(() => { load(); }, []);

  const toggleFeature = async (v) => {
    await api.put(`/admin/vendors/${v.vendor_id}/feature`, { featured: !v.featured });
    toast.success(v.featured ? "Unfeatured" : "Featured");
    load();
  };

  const togglePublish = async (v) => {
    const next = !(v.published !== false);   // treat undefined as published
    await api.put(`/admin/vendors/${v.vendor_id}/publish`, { published: next });
    toast.success(next ? "Published" : "Unpublished");
    load();
  };

  const openSuspend = (u) => {
    setSuspendUser(u);
    setSuspendReason("");
  };

  const doSuspend = async () => {
    if (!suspendUser) return;
    if (suspendReason.trim().length < 3) return toast.error("Enter a short reason");
    setSuspendBusy(true);
    try {
      await api.post(`/admin/users/${suspendUser.user_id}/suspend`, { reason: suspendReason.trim() });
      toast.success("Account suspended");
      setSuspendUser(null);
      setSuspendReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not suspend");
    } finally {
      setSuspendBusy(false);
    }
  };

  const doRestore = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/restore`);
      toast.success("Account restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not restore");
    }
  };

  // --- Derived: Vendor list after search/filter/sort ---
  // Owner-name search uses the already-loaded /admin/users payload.
  const usersById = useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(u.user_id, u);
    return m;
  }, [users]);

  const visibleVendors = useMemo(() => {
    const q = vSearch.trim().toLowerCase();
    const filtered = vendors.filter((v) => {
      if (vCategory !== "all" && (v.category || "") !== vCategory) return false;
      if (!withinDateWindow(v.created_at, vWindow)) return false;
      if (q) {
        const ownerName = (usersById.get(v.owner_id)?.name || "").toLowerCase();
        const hay = `${(v.name || "").toLowerCase()} ${ownerName}`;
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return sortDocs(filtered, vSort, (v) => v.name || "");
  }, [vendors, usersById, vSearch, vCategory, vWindow, vSort]);

  // Category options from the actual dataset — falls back to the label map.
  const vendorCategoryOptions = useMemo(() => {
    const present = Array.from(new Set(vendors.map((v) => v.category).filter(Boolean))).sort();
    return present.map((c) => ({ value: c, label: VENDOR_CATEGORY_LABELS[c] || (c[0].toUpperCase() + c.slice(1)) }));
  }, [vendors]);

  const resetVendorFilters = () => {
    setVSearch("");
    setVCategory("all");
    setVWindow("all");
    setVSort("newest");
  };

  // --- Derived: User list after search/filter/sort ---
  const visibleUsers = useMemo(() => {
    const q = uSearch.trim().toLowerCase();
    const filtered = users.filter((u) => {
      if (uRole !== "all" && (u.role || "") !== uRole) return false;
      if (!withinDateWindow(u.created_at, uWindow)) return false;
      if (q && !(u.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
    return sortDocs(filtered, uSort, (u) => u.name || "");
  }, [users, uSearch, uRole, uWindow, uSort]);

  const userRoleOptions = useMemo(() => {
    const roles = Array.from(new Set(users.map((u) => u.role).filter(Boolean))).sort();
    return roles.map((r) => ({ value: r, label: r[0].toUpperCase() + r.slice(1) }));
  }, [users]);

  const resetUserFilters = () => {
    setUSearch("");
    setURole("all");
    setUWindow("all");
    setUSort("newest");
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <div className="eyebrow mb-3">Admin</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-8 sm:mb-10">NContro Control</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-10 sm:mb-12">
        {["users", "vendors", "events", "bookings", "payments"].map(k => (
          <div key={k} className="card-elegant p-6" data-testid={`stat-${k}`}>
            <div className="eyebrow">{k}</div>
            <div className="font-serif text-4xl mt-2">{stats[k] ?? 0}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="onboarding">
        <TabsList className="bg-[#F5F2EB] flex flex-wrap h-auto">
          <TabsTrigger value="onboarding" data-testid="tab-admin-onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-admin-users">Users</TabsTrigger>
          <TabsTrigger value="verification" data-testid="tab-admin-verification">Verification</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-admin-reports" className="relative">
            Reports
            {openReportsCount > 0 && (
              <span
                data-testid="admin-reports-badge"
                className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#D9583A] text-white text-[10px] font-semibold"
              >
                {openReportsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="imports" data-testid="tab-admin-imports">Imports</TabsTrigger>
          <TabsTrigger value="ai-usage" data-testid="tab-admin-ai-usage">AI Usage</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-admin-payouts">Payouts</TabsTrigger>
          {migrationEnabled && <TabsTrigger value="migration" data-testid="tab-admin-migration">Migration</TabsTrigger>}
          <TabsTrigger value="refunds" data-testid="tab-admin-refunds" className="relative">
            Refunds
            {refundReviewCount > 0 && (
              <span
                data-testid="admin-refunds-badge"
                className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#D9583A] text-white text-[10px] font-semibold"
              >
                {refundReviewCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="onboarding" className="mt-8">
          <AdminOnboarding />
        </TabsContent>
        <TabsContent value="vendors" className="mt-8">
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="admin-vendors-filters">
            <Input
              data-testid="vendors-search"
              placeholder="Search vendor or owner name"
              value={vSearch}
              onChange={(e) => setVSearch(e.target.value)}
              className="max-w-xs bg-white"
            />
            <Select value={vCategory} onValueChange={setVCategory}>
              <SelectTrigger data-testid="vendors-category-filter" className="w-[160px] bg-white"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {vendorCategoryOptions.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={vWindow} onValueChange={setVWindow}>
              <SelectTrigger data-testid="vendors-date-filter" className="w-[160px] bg-white"><SelectValue placeholder="Any date" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any date</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="older">Older than 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={vSort} onValueChange={setVSort}>
              <SelectTrigger data-testid="vendors-sort" className="w-[160px] bg-white"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
                <SelectItem value="az">A–Z</SelectItem>
                <SelectItem value="za">Z–A</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={resetVendorFilters}
              data-testid="vendors-reset-filters"
              className="rounded-full"
            >Reset</Button>
            <span data-testid="vendors-count" className="text-xs text-[#5C5C5C] ml-auto">
              {visibleVendors.length} of {vendors.length}
            </span>
          </div>
          <div className="space-y-2">
            {visibleVendors.map(v => {
              const published = v.published !== false;
              return (
                <div key={v.vendor_id} className="card-elegant p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-vendor-${v.vendor_id}`}>
                  <div>
                    <div className="font-serif text-xl flex items-center gap-2">
                      {v.name}
                      {!published && <span className="text-[9px] uppercase tracking-widest bg-[#EFEADF] text-[#5C5C5C] px-1.5 py-0.5 rounded-full">Unpublished</span>}
                    </div>
                    <div className="text-xs text-[#5C5C5C]">{v.category} · {v.city}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button data-testid={`publish-${v.vendor_id}`} variant="outline" onClick={() => togglePublish(v)} className={`rounded-full ${!published ? "border-[color:var(--accent)] text-[color:var(--accent)]" : ""}`}>
                      {published ? "Unpublish" : "Publish"}
                    </Button>
                    <Button variant={v.featured ? "default" : "outline"} className={v.featured ? "btn-sage rounded-full" : "rounded-full"} onClick={() => toggleFeature(v)}>
                      {v.featured ? "Featured" : "Feature"}
                    </Button>
                  </div>
                </div>
              );
            })}
            {visibleVendors.length === 0 && (
              <div data-testid="vendors-empty" className="text-sm text-[#5C5C5C] py-6">No vendors match those filters.</div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="users" className="mt-8">
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="admin-users-filters">
            <Input
              data-testid="users-search"
              placeholder="Search user name"
              value={uSearch}
              onChange={(e) => setUSearch(e.target.value)}
              className="max-w-xs bg-white"
            />
            <Select value={uRole} onValueChange={setURole}>
              <SelectTrigger data-testid="users-role-filter" className="w-[160px] bg-white"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {userRoleOptions.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={uWindow} onValueChange={setUWindow}>
              <SelectTrigger data-testid="users-date-filter" className="w-[160px] bg-white"><SelectValue placeholder="Any date" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any date</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="older">Older than 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={uSort} onValueChange={setUSort}>
              <SelectTrigger data-testid="users-sort" className="w-[160px] bg-white"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
                <SelectItem value="az">A–Z</SelectItem>
                <SelectItem value="za">Z–A</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={resetUserFilters}
              data-testid="users-reset-filters"
              className="rounded-full"
            >Reset</Button>
            <span data-testid="users-count" className="text-xs text-[#5C5C5C] ml-auto">
              {visibleUsers.length} of {users.length}
            </span>
          </div>
          <div className="space-y-2">
            {visibleUsers.map(u => {
              const suspended = !!u.suspended;
              const canSuspend = u.role !== "admin";
              return (
                <div
                  key={u.user_id}
                  className="card-elegant p-4 flex items-center justify-between gap-3 flex-wrap"
                  data-testid={`admin-user-${u.user_id}`}
                >
                  <div className="min-w-0">
                    <div className="font-serif text-xl flex items-center gap-2 flex-wrap">
                      {u.name || "—"}
                      {suspended && (
                        <span
                          data-testid={`user-suspended-badge-${u.user_id}`}
                          className="text-[9px] uppercase tracking-widest bg-[#C05A46]/15 text-[#C05A46] px-1.5 py-0.5 rounded-full"
                        >
                          Suspended
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#5C5C5C]">{u.email}</div>
                    {suspended && u.suspended_reason && (
                      <div className="text-[11px] text-[#5C5C5C] mt-1 italic">
                        Reason: {u.suspended_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="eyebrow">{u.role}</div>
                    {canSuspend && (
                      suspended ? (
                        <Button
                          data-testid={`restore-user-${u.user_id}`}
                          variant="outline"
                          className="rounded-full"
                          onClick={() => doRestore(u)}
                        >Restore</Button>
                      ) : (
                        <Button
                          data-testid={`suspend-user-${u.user_id}`}
                          variant="outline"
                          className="rounded-full border-[#C05A46] text-[#C05A46] hover:bg-[#C05A46]/5"
                          onClick={() => openSuspend(u)}
                        >Suspend</Button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
            {usersError ? (
              <div data-testid="users-load-error" className="text-sm text-[color:var(--accent)] py-6 flex items-center gap-3">
                <span>Couldn't load users.</span>
                <Button data-testid="users-retry-btn" size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={loadUsers}>Retry</Button>
              </div>
            ) : visibleUsers.length === 0 && (
              <div data-testid="users-empty" className="text-sm text-[#5C5C5C] py-6">No users match those filters.</div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="verification" className="mt-8">
          <AdminVerification />
        </TabsContent>
        <TabsContent value="reports" className="mt-8">
          <AdminReports onOpenCountChange={setOpenReportsCount} />
        </TabsContent>
        <TabsContent value="imports" className="mt-8">
          <AdminImports />
        </TabsContent>
        <TabsContent value="ai-usage" className="mt-8">
          <AdminAiUsage />
        </TabsContent>
        <TabsContent value="payouts" className="mt-8">
          <AdminPayouts />
        </TabsContent>
        <TabsContent value="refunds" className="mt-8">
          <RefundReviews scope="admin" onCountChange={setRefundReviewCount} />
        </TabsContent>
        {migrationEnabled && (
          <TabsContent value="migration" className="mt-8">
            <AdminConnectMigration />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!suspendUser} onOpenChange={(o) => !o && setSuspendUser(null)}>
        <DialogContent data-testid="suspend-user-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Suspend account</DialogTitle>
            <DialogDescription className="text-xs text-[#5C5C5C]">
              {suspendUser?.role === "vendor"
                ? "The vendor's listings will be hidden from public discovery and they cannot receive new bookings. Existing bookings, payouts and messages stay in place. Planners with active bookings will be emailed."
                : "The planner cannot open new quote requests. Existing bookings remain intact — they can still cancel."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="eyebrow mb-2 block">Reason (required)</label>
              <Textarea
                data-testid="suspend-reason-input"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Brief internal reason for the audit trail"
                className="bg-white min-h-[100px]"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendUser(null)} disabled={suspendBusy} className="rounded-full">Cancel</Button>
            <Button
              onClick={doSuspend}
              disabled={suspendBusy || suspendReason.trim().length < 3}
              className="rounded-full bg-[#C05A46] hover:bg-[#A34D3B] text-white"
              data-testid="suspend-confirm-btn"
            >
              {suspendBusy ? "Suspending…" : "Suspend account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
